import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiErr, mediaUrl } from "../api";
import { useSite } from "../App";
import ImageTool from "./ImageTool";
import { BLOCK_SCHEMA, ALT_KEY } from "./Blocks";

const WIDTHS = { desktop: "100%", tablet: "768px", phone: "390px" };
const BUILTIN_PAGES = [["/", "Home"], ["/consulting", "Consulting"], ["/services", "Services"], ["/book", "Book consult"], ["/contact", "Contact"], ["/life-insurance", "Life insurance"], ["/life-insurance/options", "Coverage options"], ["/online", "Social"], ["/about", "About me"]];
const SEO_PAGES = [["consulting", "Consulting"], ["services", "Services"], ["book", "Free consult"], ["contact", "Contact"], ["start", "Start a request"], ["portal", "Client portal / login"], ["policies", "Policy pages"], ["insurance", "Life insurance"], ["social", "Social"], ["about", "About me"]];
const AREAS = [["consulting", "Consulting menu"], ["insurance", "Life insurance menu"], ["social", "Social menu"], ["about", "About me menu"]];
const IMG_LABELS = { "brand.logoUrl": "MK logo", "brand.portraitUrl": "Michael's portrait", "online.heroImage": "Social hero photo", "seo.image": "Share image" };

const getK = (obj, k) => k.split(".").reduce((o, p) => (o || {})[p], obj);
const setK = (obj, k, v) => {
  const parts = k.split("."), copy = JSON.parse(JSON.stringify(obj || {}));
  let cur = copy;
  for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || (/^\d+$/.test(parts[i + 1]) ? [] : {}); cur = cur[parts[i]]; }
  cur[parts[parts.length - 1]] = v;
  return copy;
};
const validLink = (u) => !u || u.startsWith("/") || u.startsWith("https://") || u.startsWith("#") || u.startsWith("mailto:");

export default function Editor({ onExit, initialPage = "/" }) {
  const { reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [device, setDevice] = useState("desktop");
  const [page, setPage] = useState(initialPage);
  const [media, setMedia] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [imgTarget, setImgTarget] = useState(null); // {key, current, label}
  const [seoPage, setSeoPage] = useState("consulting");
  const [panelOpen, setPanelOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const history = useRef({ past: [], future: [], lastPush: 0 });
  const iframeRef = useRef(null);
  const frameSrc = `${window.location.origin}${page}?preview=1&draft=1`;

  const loadAll = useCallback(async () => {
    try {
      const d = await apiGet("/admin/site");
      setDraft({ ...d.draft, customPages: (d.draft.customPages || []).filter((p) => !p.deleted) });
      setRevisions(d.revisions || []);
      setMedia(await apiGet("/admin/media"));
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const customPages = draft?.customPages || [];
  const pageIdx = customPages.findIndex((p) => p.path === page);

  // click-to-edit bridge from the preview iframe (text, images, links)
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === "mk-edit" || d.type === "mk-edit-image") {
        setPanelOpen(true);
        const m = /^customPages\.(\d+)\./.exec(d.key);
        if (m) setDraft((dr) => { const p = (dr?.customPages || [])[Number(m[1])]; if (p && p.path !== page) setPage(p.path); return dr; });
        if (d.type === "mk-edit-image") { setDraft((dr) => { setImgTarget({ key: d.key, current: getK(dr, d.key), label: d.label || IMG_LABELS[d.key] || "Image" }); return dr; }); return; }
        setTimeout(() => {
          const el = document.getElementById(`ef-${d.key}`);
          if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); el.classList.add("flash"); setTimeout(() => el.classList.remove("flash"), 1200); }
        }, 150);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [page]);

  const reloadFrame = () => { if (iframeRef.current) iframeRef.current.src = frameSrc; };
  const pushHistory = (prev) => {
    const h = history.current, now = Date.now();
    if (now - h.lastPush > 700) { h.past = [...h.past.slice(-40), prev]; h.future = []; }
    h.lastPush = now;
  };
  const edit = (k, v) => { setDraft((d) => { pushHistory(d); return setK(d, k, v); }); setDirty(true); };
  const editArr = edit;
  const undo = () => { const h = history.current; if (!h.past.length) return; setDraft((d) => { h.future.push(d); const p = h.past.pop(); h.lastPush = 0; return p; }); setDirty(true); };
  const redo = () => { const h = history.current; if (!h.future.length) return; setDraft((d) => { h.past.push(d); return h.future.pop(); }); setDirty(true); };
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const saveDraft = async (silent, d = draft) => {
    setSaving(true);
    try { await apiPost("/admin/site/draft", { site: d }); setDirty(false); if (!silent) toast.success("Draft saved privately."); reloadFrame(); return true; }
    catch (e) { toast.error(apiErr(e)); return false; } finally { setSaving(false); }
  };
  const publish = async () => {
    try {
      if (dirty) await apiPost("/admin/site/draft", { site: draft });
      await apiPost("/admin/site/publish"); setDirty(false); toast.success("Published. The public site is updated."); await reload(); await loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const revert = async (revisionId) => {
    if (!window.confirm("Restore this revision as the live site?")) return;
    try { await apiPost("/admin/site/revert", { revisionId }); toast.success("Revision restored."); await reload(); await loadAll(); reloadFrame(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const discard = async () => {
    if (!window.confirm("Discard all unsaved draft changes and reset the draft to the published site?")) return;
    try { await apiPost("/admin/site/discard-draft"); await loadAll(); setDirty(false); reloadFrame(); toast.success("Draft reset to published version."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  // Images are saved to the draft immediately so they survive reloads even before Publish.
  const altKeyFor = (key) => {
    if (key === "brand.portraitUrl") return "brand.portraitAlt";
    if (key === "brand.logoUrl") return "brand.logoAlt";
    if (key === "online.heroImage") return "online.heroImageAlt";
    if (key === "seo.image") return null;
    const m = /^(.*)\.(url|image|cover)$/.exec(key);
    return m ? `${m[1]}.${ALT_KEY[m[2]]}` : null;
  };
  const setImage = (url, alt = "") => {
    let next;
    if (/^online\.gallery\.\d+$/.test(imgTarget.key)) next = setK(draft, imgTarget.key, url ? { url, alt } : "");
    else {
      next = setK(draft, imgTarget.key, url);
      const ak = altKeyFor(imgTarget.key);
      if (ak) next = setK(next, ak, alt);
    }
    pushHistory(draft); setDraft(next); setDirty(true); setImgTarget(null);
    saveDraft(true, next).then((ok) => { if (ok) toast.success(url ? "Image saved to your draft. Publish to make it live." : "Image removed from the draft."); });
  };
  const openImg = (key, label, extra = {}) => {
    const cur = getK(draft, key);
    const isObj = cur && typeof cur === "object";
    const ak = altKeyFor(key);
    setImgTarget({ key, current: isObj ? cur.url : cur, alt: isObj ? cur.alt : (ak ? getK(draft, ak) : "") || "", label, ...extra });
  };
  const saveTemplate = async () => {
    const name = window.prompt("Name this layout so you can reuse it for new pages:", `${cp.title} layout`);
    if (!name) return;
    try { if (dirty) await saveDraft(true); await apiPost("/admin/templates", { pageId: cp.id, name }); toast.success("Layout saved as a template."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!draft) return <div className="loading">Opening the website editor…</div>;
  const T = (label, k, area, hint) => (
    <div className="editor-field"><label>{label}</label>
      {area ? <textarea id={`ef-${k}`} value={getK(draft, k) || ""} onChange={(e) => edit(k, e.target.value)} />
        : <input id={`ef-${k}`} value={getK(draft, k) || ""} onChange={(e) => edit(k, e.target.value)} />}
      {hint && <p className="tiny muted">{hint}</p>}</div>
  );
  const L = (label, k) => (
    <div className="editor-field"><label>{label}</label>
      <input id={`ef-${k}`} value={getK(draft, k) || ""} onChange={(e) => edit(k, e.target.value.trim())} placeholder="/page or https://" />
      {!validLink(getK(draft, k)) && <p className="tiny" style={{ color: "var(--error)" }}>Use /page for an internal link or https:// for an external one.</p>}</div>
  );
  const ImgField = (label, k) => {
    const cur = getK(draft, k);
    return (<div className="editor-field"><label>{label}</label>
      <button type="button" className={`img-slot${cur ? "" : " empty"}`} onClick={() => openImg(k, label)} data-testid={`img-${k.replace(/\./g, "-")}`}>
        {cur ? <img src={mediaUrl(cur) || cur} alt="" /> : <span>Click to add a photo</span>}
      </button></div>);
  };
  const move = (arr, i, d) => { const a = [...arr], j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };
  const Ctl = ({ k, arr, i, testid, locked }) => (
    <div className="row rep-ctl">
      <button type="button" className="text-button" disabled={i === 0} onClick={() => editArr(k, move(arr, i, -1))} aria-label="Move up" data-testid={`${testid}-up-${i}`}>▲ Up</button>
      <button type="button" className="text-button" disabled={i === arr.length - 1} onClick={() => editArr(k, move(arr, i, 1))} aria-label="Move down" data-testid={`${testid}-down-${i}`}>▼ Down</button>
      {!locked && <button type="button" className="text-button danger" onClick={() => editArr(k, arr.filter((_, x) => x !== i))} data-testid={`${testid}-remove-${i}`}>Remove</button>}
    </div>
  );
  const LinkList = ({ label, k, testid }) => {
    const arr = getK(draft, k) || [];
    return (
      <div className="editor-field"><label>{label}</label>
        <p className="tiny muted">Internal pages: start with "/" (for example /online or /contact). Other sites: full https:// address.</p>
        {arr.map((c, i) => (
          <div key={i} className="rep-item">
            <div className="editor-field row2">
              <input placeholder="Label" value={c.label || ""} onChange={(e) => editArr(k, arr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`${testid}-label-${i}`} />
              <input placeholder="/page or https://" value={c.url || ""} onChange={(e) => editArr(k, arr.map((x, j) => j === i ? { ...x, url: e.target.value.trim() } : x))} data-testid={`${testid}-url-${i}`} />
            </div>
            {!validLink(c.url) ? <p className="tiny" style={{ color: "var(--error)" }}>Use /page for an internal link or https:// for an external one.</p> : null}
            <Ctl k={k} arr={arr} i={i} testid={testid} />
          </div>))}
        <button type="button" className="text-button" onClick={() => editArr(k, [...arr, { label: "", url: "" }])} data-testid={`${testid}-add`}>+ Add link</button>
      </div>);
  };
  const Gallery = ({ label, k, testid, withCaption }) => {
    const arr = getK(draft, k) || [];
    const urlOf = (g) => (g && typeof g === "object" ? g.url : g);
    return (
      <div className="editor-field"><label>{label}</label>
        {arr.map((g, i) => (
          <div key={i} className="rep-item">
            <button type="button" className="img-slot" onClick={() => openImg(withCaption ? `${k}.${i}.url` : `${k}.${i}`, `${label} — photo ${i + 1}`, { allowRemove: false })} data-testid={`${testid}-replace-${i}`}>
              <img src={mediaUrl(urlOf(g)) || urlOf(g)} alt="" /></button>
            {withCaption && <input placeholder="Caption" value={g.caption || ""} onChange={(e) => editArr(k, arr.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} data-testid={`${testid}-caption-${i}`} />}
            <Ctl k={k} arr={arr} i={i} testid={testid} />
          </div>))}
        <button type="button" className="text-button" onClick={() => { const next = [...arr, withCaption ? { url: "", caption: "" } : ""]; edit(k, next); openImg(withCaption ? `${k}.${arr.length}.url` : `${k}.${arr.length}`, `${label} — new photo`, { allowRemove: false }); }} data-testid={`${testid}-add`}>+ Add photo (upload)</button>
      </div>);
  };
  const MenuEditor = ({ area, label }) => {
    const k = `menus.${area}`, arr = getK(draft, k) || [];
    return (<div className="editor-field"><label>{label}</label>
      {arr.map((it, i) => (
        <div key={i} className={`rep-item${it.hidden ? " dim" : ""}`}>
          <div className="editor-field row2">
            <input value={it.label || ""} onChange={(e) => edit(k, arr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`menu-${area}-label-${i}`} />
            <input value={it.url || ""} disabled={!!it.key} onChange={(e) => edit(k, arr.map((x, j) => j === i ? { ...x, url: e.target.value.trim() } : x))} data-testid={`menu-${area}-url-${i}`} />
          </div>
          <div className="row between">
            <label className="check tiny"><input type="checkbox" checked={!it.hidden} onChange={(e) => edit(k, arr.map((x, j) => j === i ? { ...x, hidden: !e.target.checked } : x))} data-testid={`menu-${area}-show-${i}`} /><span>Show in menu</span></label>
            <Ctl k={k} arr={arr} i={i} testid={`menu-${area}`} locked={!!it.key} />
          </div>
        </div>))}
      <button type="button" className="text-button" onClick={() => edit(k, [...arr, { label: "New link", url: "/" }])} data-testid={`menu-${area}-add`}>+ Add menu link</button>
      <p className="tiny muted">Built-in items can be renamed, hidden, or reordered. Pages you create appear in the menu automatically (control that from Pages in the dashboard).</p>
    </div>);
  };
  const BlockEditor = ({ pi }) => {
    const k = `customPages.${pi}.blocks`, arr = getK(draft, k) || [];
    const F = (base, fields, testid) => fields.map(([f, label, kind, opts]) => {
      const fk = `${base}.${f}`, v = getK(draft, fk) || "";
      if (kind === "image") return <button key={f} type="button" className={`img-slot${v ? "" : " empty"}`} onClick={() => openImg(fk, label)} data-testid={`${testid}-${f}`}>{v ? <img src={mediaUrl(v) || v} alt="" /> : <span>{label}: click to add a photo</span>}</button>;
      if (kind === "select") return <div key={f} className="editor-field"><label>{label}</label><select id={`ef-${fk}`} value={v || opts[0][0]} onChange={(e) => edit(fk, e.target.value)} data-testid={`${testid}-${f}`}>{opts.map(([ov, ol]) => <option key={ov} value={ov}>{ol}</option>)}</select></div>;
      if (kind === "area") return <textarea key={f} id={`ef-${fk}`} placeholder={label} value={v} onChange={(e) => edit(fk, e.target.value)} data-testid={`${testid}-${f}`} />;
      return <div key={f}><input id={`ef-${fk}`} placeholder={label} value={v} onChange={(e) => edit(fk, kind === "link" ? e.target.value.trim() : e.target.value)} data-testid={`${testid}-${f}`} />
        {kind === "link" && !validLink(v) && <p className="tiny" style={{ color: "var(--error)" }}>Use /page for an internal link or https:// for an external one.</p>}</div>;
    });
    return (<div className="editor-field"><label>Content blocks</label>
      {arr.map((bl, i) => {
        const sc = BLOCK_SCHEMA[bl.type] || BLOCK_SCHEMA.text, base = `${k}.${i}`;
        const items = sc.items ? (bl[sc.items.key] || []) : null;
        return (<div key={bl.id || i} className="rep-item" data-testid={`block-${i}`}>
          <span className="tiny muted" style={{ textTransform: "uppercase", letterSpacing: ".08em" }}>{sc.label}</span>
          {F(base, sc.fields, `block-${i}`)}
          {sc.items && <div className="rep-sub">
            {items.map((it, j) => <div key={j} className="rep-item"><span className="tiny muted">Item {j + 1}</span>{F(`${base}.${sc.items.key}.${j}`, sc.items.fields, `block-${i}-item-${j}`)}
              <Ctl k={`${base}.${sc.items.key}`} arr={items} i={j} testid={`block-${i}-item`} /></div>)}
            <button type="button" className="text-button" onClick={() => edit(`${base}.${sc.items.key}`, [...items, { ...sc.items.blank }])} data-testid={`block-${i}-add-item`}>+ Add item</button>
          </div>}
          <Ctl k={k} arr={arr} i={i} testid="block" />
        </div>);
      })}
      <div className="row" style={{ flexWrap: "wrap" }}>{Object.entries(BLOCK_SCHEMA).map(([t, sc]) => <button key={t} type="button" className="text-button" onClick={() => edit(k, [...arr, { id: `blk_${Date.now()}`, type: t, ...(sc.items ? { [sc.items.key]: [{ ...sc.items.blank }] } : {}) }])} data-testid={`block-add-${t}`}>+ {sc.label}</button>)}</div>
    </div>);
  };

  const cp = pageIdx >= 0 ? customPages[pageIdx] : null;
  return (
    <div className="editor-shell">
      <div className="editor-bar">
        <div className="grp">
          <button className="only-mobile" onClick={() => setPanelOpen((o) => !o)} data-testid="editor-toggle-panel">{panelOpen ? "Preview" : "Fields"}</button>
          <strong>Edit Website</strong>
          <span className="dot" data-testid="editor-status">{saving ? "Saving…" : dirty ? "Unsaved draft changes" : "Draft saved"}</span>
        </div>
        <div className="grp hide-mobile">
          {Object.keys(WIDTHS).map((d) => <button key={d} className={device === d ? "active" : ""} onClick={() => setDevice(d)} data-testid={`device-${d}`}>{d}</button>)}
        </div>
        <div className="grp">
          <button onClick={undo} disabled={!history.current.past.length} title="Undo (Ctrl+Z)" data-testid="editor-undo">Undo</button>
          <button onClick={redo} disabled={!history.current.future.length} title="Redo (Ctrl+Shift+Z)" data-testid="editor-redo">Redo</button>
          <button onClick={() => saveDraft(false)} data-testid="editor-save-draft">Save Draft</button>
          <button onClick={reloadFrame} data-testid="editor-preview">Preview</button>
          <button className="primary" onClick={publish} data-testid="editor-publish">Publish</button>
          <button onClick={() => setShowHistory((s) => !s)} data-testid="editor-history">History</button>
          <button onClick={onExit} data-testid="editor-exit">Exit</button>
        </div>
      </div>
      <div className={`editor-body${panelOpen ? " panel-open" : ""}`}>
        <div className="editor-panel">
          {showHistory && <div className="card" style={{ marginBottom: 16 }} data-testid="revision-history">
            <h3>Revision history</h3>
            {revisions.length ? revisions.map((r) => (
              <div className="rev-item" key={r.id}><span>{r.label || r.at}</span>
                <button className="text-button" onClick={() => revert(r.id)} data-testid={`revert-${r.id}`}>Restore</button></div>
            )) : <p className="tiny muted">Publish to create your first restore point.</p>}
            <button className="text-button danger" onClick={discard} data-testid="editor-discard">Reset draft to the published site</button>
          </div>}
          <h3>Page</h3>
          <div className="editor-field"><select value={page} onChange={(e) => setPage(e.target.value)} data-testid="editor-page-select">
            <optgroup label="Built-in pages">{BUILTIN_PAGES.map(([u, l]) => <option key={u} value={u}>{l}</option>)}</optgroup>
            {customPages.length ? <optgroup label="Your pages">{customPages.map((p) => <option key={p.id} value={p.path}>{p.title}{p.status !== "published" ? " (hidden)" : ""}</option>)}</optgroup> : null}
          </select></div>
          <p className="tiny muted">Click any outlined text, photo, or button in the preview to edit it here. Forms in preview do not submit, charge, or email.</p>

          {cp && <>
            <h3>{cp.title}</h3>
            <p className="tiny muted">URL: {cp.path} · {cp.status === "published" ? "Live" : "Hidden from visitors"}. Rename, move, or change the URL from Pages in the dashboard.</p>
            {T("Eyebrow", `customPages.${pageIdx}.hero.eyebrow`)}
            {T("Page headline", `customPages.${pageIdx}.hero.title`, true)}
            {T("Intro", `customPages.${pageIdx}.hero.lead`, true)}
            {ImgField("Hero image", `customPages.${pageIdx}.hero.image`)}
            <BlockEditor pi={pageIdx} />
            <div className="row"><button type="button" className="button secondary small" onClick={saveTemplate} data-testid="save-template-btn">Save this layout as a template</button></div>
            {T("Browser tab title", `customPages.${pageIdx}.seo.title`)}
            {T("Meta description", `customPages.${pageIdx}.seo.description`, true)}
          </>}

          <h3>Menus</h3>
          {AREAS.map(([a, l]) => <MenuEditor key={a} area={a} label={l} />)}

          <h3>Brand &amp; logo</h3>
          {T("Business name", "brand.businessName")}
          {T("Brand line (header/footer)", "brand.brandLine", true)}
          {ImgField("Logo", "brand.logoUrl")}
          {ImgField("Portrait (Michael's photo)", "brand.portraitUrl")}

          <h3>Hero section</h3>
          {T("Eyebrow", "hero.eyebrow")}
          {T("Headline", "hero.title", true)}
          {T("Lead paragraph", "hero.lead", true)}
          {T("Primary button label", "hero.primaryLabel")}
          {L("Primary button link", "hero.primaryUrl")}
          {T("Secondary button label", "hero.secondaryLabel")}
          {L("Secondary button link", "hero.secondaryUrl")}
          {T("Note under buttons", "hero.note")}
          {T("Art card title", "hero.cardTitle")}
          {T("Art card text", "hero.cardText", true)}

          <h3>Services section</h3>
          {T("Eyebrow", "servicesSection.eyebrow")}
          {T("Title", "servicesSection.title", true)}
          {T("Intro", "servicesSection.intro", true)}

          <h3>About section</h3>
          {T("Eyebrow", "aboutSection.eyebrow")}
          {T("Title", "aboutSection.title", true)}
          {T("Role label", "aboutSection.role")}

          <h3>Footer</h3>
          {T("Footer tagline", "footer.tagline")}

          <h3>About me page</h3>
          {T("Title", "about.title", true)}
          {T("Intro", "about.intro", true)}
          {T("Story heading", "about.storyTitle")}
          {T("My story", "about.story", true)}
          {T("Photos heading", "about.photosTitle")}
          {T("Photos note", "about.photosNote", true)}
          <Gallery label="Photo gallery" k="about.gallery" testid="about-photo" withCaption />
          {T("Connect heading", "about.connectTitle")}
          {T("Connect note", "about.connectNote", true)}
          <LinkList label="Connect links (only ones you add appear)" k="about.connect" testid="about-link" />

          <h3>Social page</h3>
          {T("Eyebrow", "online.eyebrow")}
          {T("Headline", "online.title", true)}
          {T("Intro", "online.intro", true)}
          {ImgField("Hero photo", "online.heroImage")}
          <Gallery label="Moments gallery" k="online.gallery" testid="social-photo" />
          {T("Membership tagline (shown until a tier is published)", "online.membershipTagline")}
          {T("Support tagline (shown until support is switched on)", "online.supportTagline")}
          <LinkList label="Social links (verified links only)" k="online.socials" testid="social-link" />

          <h3>SEO &amp; sharing</h3>
          {T("Personal home page title", "seo.title")}
          {T("Personal home description", "seo.description", true)}
          {ImgField("Share image (used for link previews)", "seo.image")}
          <div className="editor-field"><label>Per-page title &amp; description</label>
            <select value={seoPage} onChange={(e) => setSeoPage(e.target.value)} data-testid="seo-page-select">
              {SEO_PAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          {T("Browser tab title", `seo.pages.${seoPage}.title`)}
          {T("Meta description", `seo.pages.${seoPage}.description`, true)}

          <h3>Colors</h3>
          {["ink", "gold", "paper"].map((c) => (
            <div className="color-row" key={c}>
              <input type="color" value={getK(draft, `theme.${c}`) || "#000000"} onChange={(e) => edit(`theme.${c}`, e.target.value)} data-testid={`color-${c}`} />
              <span className="small">{c === "ink" ? "Primary / navy" : c === "gold" ? "Accent / gold" : "Background / ivory"}</span>
            </div>))}

          <p className="tiny muted spaced">Prices, issued invoices, subscriptions, and accepted agreements are controlled in the dashboard tabs — a visual edit here never changes them.</p>
        </div>
        <div className="editor-preview">
          <iframe ref={iframeRef} className="editor-frame" title="Website preview" src={frameSrc} style={{ width: WIDTHS[device] }} data-testid="editor-iframe" />
        </div>
      </div>
      {imgTarget && <ImageTool target={imgTarget} media={media} onDone={setImage} onClose={() => setImgTarget(null)} onMediaChange={async () => setMedia(await apiGet("/admin/media"))} />}
    </div>
  );
}
